import { createAsyncThunk, ThunkAction } from '@reduxjs/toolkit';
import type { AnyAction } from 'redux';
import { NavigateFunction } from 'react-router-dom';
import { z } from 'zod';
import { AppDispatch, RootState } from '../app/store';

//	------------------------------------------------------------
//
//	hold details on a data-management job error.
//
//	------------------------------------------------------------

export interface JobErrorType {
  message: string;
  created_at: string;
} // JobItemType

//	------------------------------------------------------------
//
//	hold details on a container, dataset, or file.
//
//	------------------------------------------------------------

export interface JobItemType {
  scope: string;
  name: string;
  type: string;
  bytes: number;
  adler32: string;
  md5: string;
  unauthorised?: boolean;
} // JobItemType

//	------------------------------------------------------------
//
//	hold details on data-management jobs.
//
//	------------------------------------------------------------

export interface JobType {
  row_id: number;
  job_id: string;
  to_storage_area_uuid: string;
  to_node: string;
  to_site: string;
  to_site_uuid: string;
  to_storage_identifier: string;
  status: string;
  parent_namespace: string;
  parent_name: string;
  containers: JobItemType[];
  datasets: JobItemType[];
  files: JobItemType[];
  num_items: number;
  expires_at: string;
  expires_in: string;
  updated_at: string;
  error_message?: JobErrorType;
  skaha_session_id?: string;
} // JobType

//	------------------------------------------------------------
//
//	hold details on notifications.
//
//	------------------------------------------------------------

export interface NotificationType {
  notificationID: number;
  notificationType: string;
  notificationText: string;
  readFlag: boolean;
  additionalInfo: any;
  createdAt: string;
  displayPopup: boolean;
  sseExtras?: any;
} // NotificationType

//	------------------------------------------------------------
//
//	hold node, site, and site_uuid for a single site.
//
//	------------------------------------------------------------

export interface Site {
  node?: string;
  site?: string;
  site_uuid?: string;
  icon?: string;
  iconBW?: string;
  index?: number;
  admin?: boolean;
} // Site

//	------------------------------------------------------------
//
//	hold details on the storage locations for each site
//
//	------------------------------------------------------------

export interface StorageArea {
  storage_id: string;
  storage_type: string;
  relative_path: string;
  name: string;
} // StorageArea

export interface SiteStorageArea {
  site: Site;
  storage_areas: StorageArea[];
} // SiteStorageArea

//	------------------------------------------------------------
//
//	hold details on an associated service. this is the object
//	returned from the /site_capabilities/list_compute end
//	point.
//
//	------------------------------------------------------------

export interface ServiceDetails {
  id: string;
  type: string;
  prefix: string;
  host: string;
  path: string;
  name: string;
  is_force_disabled: boolean;
  downtime: string[];
  port: number;
  active_status: boolean;
  health_status: string;
  top_banner: string;
  bottom_banner_type: string;
  bottom_banner: string;
  active_session_count: number;
} // ServiceDetails

//	------------------------------------------------------------
//
//	hold details for single data query
//
//	------------------------------------------------------------

export interface ResolveName {
  ra: number;
  dec: number;
  description: string;
}

//	------------------------------------------------------------
//
//	hold details for single data query
//
//	------------------------------------------------------------

export interface DataQuery {
  data_query_id: number;
  project: number;
  name: string;
  data_query: string;
  last_updated_at: string;
}

//	------------------------------------------------------------
//
//	hold details for data queries
//
//	------------------------------------------------------------

export interface APIResponse<T> {
  status: string;
  message: string;
  data_queries: T[];
}

//	------------------------------------------------------------
//
//	For saving a new data query
//
//	------------------------------------------------------------

export interface SaveQueryRequest {
  project_id: number | undefined;
  name: string;
  query_param: string;
}

export interface SaveQueryResponse {
  status: string;
  message: string;
}

export enum UserType {
  STANDARD,
  OPERATOR,
  DEVELOPER,
} // UserType

//	------------------------------------------------------------
//
//	the currently selected tool from the home page.
//
//	------------------------------------------------------------

export enum ToolType {
  NONE,
  HOME,
  SEARCH_CATALOG,
  SEARCH_COMPUTE,
  COMPUTE_JOBS,
  FILE_BROWSER,
  USER_MANAGEMENT,
  WORKFLOW,
  TERMINAL,
  OIDC_TOKENS,
  VIEW_NOTIFICATIONS,
  LOGS,
  DATA,
  COMPUTE,
  INTERACTIVE_SESSIONS,
  DATA_TRANSFER_JOBS,
  USER_PROFILE,
  SEARCH_SOFTWARE,
  PROJECT,
  SEARCH_CATALOG_CAOM,
} // ToolType

//	------------------------------------------------------------
//
//	the currently selected theme.
//
//	------------------------------------------------------------

export enum Theme {
  LIGHT,
  DARK,
  AUTO,
} // Theme

//  ------------------------------------------------------------
//
//  External global APIs.
//
//  ------------------------------------------------------------

export enum API {
  AUTHENTICATION = 'AUTHENTICATION',
  DATA_MANAGEMENT = 'DATA_MANAGEMENT',
  GATEWAY_BACKEND = 'GATEWAY_BACKEND',
  IAM = 'IAM',
  PERMISSIONS = 'PERMISSIONS',
  RUCIO = 'RUCIO',
  SITE_CAPABILITIES = 'SITE_CAPABILITIES',
}

//  ------------------------------------------------------------
//
//  Valid statuses of external global APIs.
//
//  ------------------------------------------------------------

export enum APIStatus {
  RUNNING = 'RUNNING',
  STOPPED = 'STOPPED',
  UNKNOWN = 'UNKNOWN',
}

//  ------------------------------------------------------------
//
//  Object containing the status for a single external global
//  API.
//
//  ------------------------------------------------------------

export interface APIStatusItem {
  api: API;
  status: APIStatus;
}

//	------------------------------------------------------------
//
//	For service statuses
//
//	------------------------------------------------------------

export interface AvailabilityOption {
  label: string;
  value: string;
}

export interface ComputeServiceType {
  id?: number; // holds the database table ID for an active SRCNet session. set to -1 for available services and active CANFAR sessions.
  site: Site;
  resource_type?: string;
  session_type: string;
  compute_service_id: string; // holds the SRCNet compute service ID or an active CANFAR session ID. blank for an available canfar service.
  service_name: string; // i.e. CARTA, Virtual Desktop
  session_name?: string; // i.e. carta-session-12345
  service_url: string;
  host_url?: string; // for CANFAR services, this will be the url without the /science-portal address postfixed, to be used for querying SKAHA/cavern.
  notebook_path?: string;
  started_at?: string;
  expires_at?: string;
  is_embedded: boolean;
  compute_status: string; // available, running, pending, unavailable
  overridden_status?: string; // starting, removing, renewing
  active_status?: boolean;
  active_session_count?: number;
  top_banner?: string;
  health_status?: string;
  bottom_banner_type?: string;
  bottom_banner?: string;
  is_force_disabled?: boolean;
  downtime?: string[];
  skahaImages?: string[];
  refreshing?: boolean;
}

export interface CanfarServiceType {
  id: string;
  url: string;
  site: Site;
  name: string;
  images?: {
    id: string;
    types: string[];
  }[];
}

export enum DataItemEnabledCondition {
  //
  SELECTED_CANFAR,
  //
} // DataItemEnabledCondition

//  ------------------------------------------------------------
//
//  Holds the details of a single data item, including
//  namespace and filename.
//
//  ------------------------------------------------------------

export interface DataItemType {
  //
  id?: number;
  namespace: string;
  filename: string;
  obs_publisher_did?: string;
  target_name?: string;
  did_type?: string;
  bytes?: number;
  enabled?: DataItemEnabledCondition;
  //
} // DataItemType

//  ------------------------------------------------------------
//
//  Holds the details of a single data item from cavern.
//
//  ------------------------------------------------------------

export interface CavernFileType {
  //
  filename: string;
  fileType?: string;
  modifiedDate?: string;
  length?: number;
  storagePath?: string;
  //
} // CavernFileType

//  ------------------------------------------------------------
//
//  Holds the current page displayed on the file browser.
//
//  ------------------------------------------------------------

export enum FileBrowserPage {
  None,
  FileBrowser,
  Collections,
  Namespaces,
  UserStorage,
  ProjectStorage,
  Items,
  Dataset,
  JobDetails,
  Collection,
} // FileBrowserPage

//	------------------------------------------------------------
//
//	Log entry format
//
//	------------------------------------------------------------

export interface GatewayLogMetadataType {
  job_id?: string | null;
  site_only?: boolean | null;
  site_uuid?: string | null;
  // Frontend only fields.
  repeat_count?: number;
  repeat_dates?: string[];
  displayPopup?: boolean;
}

export interface GatewayLogType {
  client_id: string;
  log_id: string;
  user_id: number | null;
  level: number;
  message: string;
  component: string;
  created_at: string;
  metadata: GatewayLogMetadataType;
}

//	------------------------------------------------------------
//
//	Timestamp range options for logs.
//
//	------------------------------------------------------------

export enum GatewayLogTimestampRange {
  SESSION = 'SESSION',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
  ALL = 'ALL',
}

export interface LaunchCallbacks {
  onStart?: () => void;
  onSuccess?: (result: any) => void;
  onError?: (err: any) => void;
  onFinish?: () => void;
}

//	------------------------------------------------------------
//
//	API Error Schema
//
//	------------------------------------------------------------
export const ApiErrorSchema = z.object({
  detail: z.any(),
  status: z.number(),
  statusText: z.string(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

//	------------------------------------------------------------
//
//	Project
//
//	------------------------------------------------------------

export interface Project {
  id: number;
  name: string;
  description?: string;
  abbreviation?: string;
  icon?: string;
  site_uuid?: string;
}

export interface LanguageMenuProps {
  setLanguageDropdownMenuDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export interface ToolDescription {
  text: string;
  toolType: ToolType;
  link: string;
  subItems: ToolDescription[];
  needSiteCapabilities?: boolean;
  needDataManagement?: boolean;
  needLoggedIn?: boolean;
  needJupyterURL?: boolean;
  needOperatorUser?: boolean;
  needDeveloperUser?: boolean;
}

//	------------------------------------------------------------
//
//	Define an object that holds a list of data items, and
//	for each item a list of sites where that item is available,
//	and a list of sites where that item is currently being
//	staged.
//
//	------------------------------------------------------------

export type RucioLocationsType = {
  //
  namespace: string;
  filename: string;
  availableSites: Site[];
  movingSites: Site[];
  userCanAccessNamespace?: boolean;
  bytes?: number;
  refresh?: boolean;
  lastChecked?: string;
  refreshingLocations: boolean;
  catalog: string[]; // allows us to keep separate catalogs of files, i.e. one for data-discovery selected items ('search-catalog'), and one for home-area project items ('project-files').
  //
}; // RucioLocationsType

export const toolTypeToPath: Record<ToolType, string> = {
  [ToolType.NONE]: '#',
  [ToolType.HOME]: '/home',
  [ToolType.SEARCH_CATALOG]: '/search-catalog',
  [ToolType.SEARCH_COMPUTE]: '/search-compute',
  [ToolType.COMPUTE_JOBS]: '/compute-jobs',
  [ToolType.FILE_BROWSER]: '/data-management',
  [ToolType.USER_MANAGEMENT]: '/user-management',
  [ToolType.VIEW_NOTIFICATIONS]: '/notifications',
  [ToolType.LOGS]: '/logs',
  [ToolType.OIDC_TOKENS]: '/developer-tokens',
  [ToolType.WORKFLOW]: '#',
  [ToolType.TERMINAL]: '#',
  [ToolType.DATA]: '#',
  [ToolType.COMPUTE]: '#',
  [ToolType.INTERACTIVE_SESSIONS]: '/interactive-sessions',
  [ToolType.DATA_TRANSFER_JOBS]: '/data-transfer-jobs',
  [ToolType.USER_PROFILE]: '/user-profile',
  [ToolType.SEARCH_SOFTWARE]: '/search-software',
  [ToolType.PROJECT]: '/project',
  [ToolType.SEARCH_CATALOG_CAOM]: '/search-catalog-caom',
};

export interface NavigateEventContext {
  navigate?: NavigateFunction;
  showSuccessToast?: (message: string) => void;
  showWarningToast?: (message: string) => void;
  showErrorToast?: (message: string) => void;
}

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  AnyAction
>;

export const createAppAsyncThunk = createAsyncThunk.withTypes<{
  state: RootState;
  dispatch: AppDispatch;
}>();
